/**
 * billDate — the rules for a Quick Bill's hand-entered date.
 *
 * ── Where the date lives ────────────────────────────────────────────────────
 * An invoice has no separate "bill date" column: `invoices.created_at` IS the
 * bill date. The printed invoice, every sales report, GSTR-1/3B, the Tally
 * export and day-close all read it, and sync carries it through unchanged
 * (the server keeps a device's `createdAt` because it is the business time of
 * an offline sale). So a Quick Bill sets `created_at` to the chosen day and
 * everything downstream follows — no schema change. The moment the bill was
 * actually made goes into the Activity Log instead.
 *
 * ── Time of day ─────────────────────────────────────────────────────────────
 * The date picker gives a day. The bill takes the current time of day on that
 * day, so several Quick Bills made in a row keep their order within the day,
 * and a bill dated today is simply "now".
 *
 * ── The one hard rule ───────────────────────────────────────────────────────
 * A Quick Bill is a real GST sale in the normal invoice series (owner's
 * decision, 2026-09-29), and any date is allowed — past or future. What is
 * refused is a date inside a month whose return is recorded as FILED: that
 * bill would silently change a return already submitted to the government.
 *
 * Pure: no database, no clock except the `now` passed in.
 */

/** 'YYYY-MM-DD' for a local date. */
export function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parses 'YYYY-MM-DD' as a LOCAL calendar day; null when malformed. */
export function parseDateInput(value: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const date = new Date(y, mo, d);
  // Rejects 2026-02-31 and friends, which Date would roll into March.
  if (date.getFullYear() !== y || date.getMonth() !== mo || date.getDate() !== d) return null;
  return date;
}

/**
 * The timestamp a Quick Bill is written with: the chosen day at the current
 * time of day. A missing or malformed day means today, i.e. `now`.
 */
export function billTimestamp(billDate: string | null | undefined, now: Date = new Date()): number {
  const day = parseDateInput(billDate);
  if (!day) return now.getTime();
  day.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  return day.getTime();
}

/** GST return period for a timestamp: 'YYYY-MM' in local time. */
export function billPeriod(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Whole calendar days from today to the bill date: negative = back-dated. */
export function daysFromToday(billDate: string | null | undefined, now: Date = new Date()): number {
  const day = parseDateInput(billDate);
  if (!day) return 0;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((day.getTime() - today.getTime()) / 86_400_000);
}

export interface FilingLike {
  period: string;
  returnType: string;
  status: string;
  isDeleted?: boolean;
}

/**
 * The return that makes a period closed to new bills, or null when the period
 * is open. Only a record whose status is exactly 'FILED' closes it — a draft
 * or pending record does not.
 */
export function filedReturnFor(period: string, filings: readonly FilingLike[]): string | null {
  const hit = filings.find((f) => f.period === period && f.status === 'FILED' && !f.isDeleted);
  return hit ? hit.returnType : null;
}

/** 'Sep 2026' for 'YYYY-MM'. */
export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/** Why a bill cannot be dated into this period, or null when it can. */
export function filedPeriodError(period: string, filings: readonly FilingLike[]): string | null {
  const ret = filedReturnFor(period, filings);
  if (!ret) return null;
  const name = ret === 'GSTR1' ? 'GSTR-1' : ret === 'GSTR3B' ? 'GSTR-3B' : ret;
  return (
    `${name} for ${periodLabel(period)} is already marked as filed. ` +
    `A bill can't be dated into a month whose return has been filed — pick a date in an open month.`
  );
}

/**
 * One line for the operator about what the chosen date means, or null for
 * today. Shown under the date field so a back- or forward-dated bill is never
 * a surprise in reports.
 */
export function billDateNotice(billDate: string | null | undefined, now: Date = new Date()): string | null {
  const offset = daysFromToday(billDate, now);
  if (offset === 0) return null;
  const day = parseDateInput(billDate)!;
  const label = day.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const period = periodLabel(billPeriod(day.getTime()));
  const when = offset < 0
    ? `${-offset} day${offset === -1 ? '' : 's'} ago`
    : `${offset} day${offset === 1 ? '' : 's'} ahead`;
  return `Dated ${label} (${when}). It will count in that day's sales and in the ${period} GST return.`;
}
